import { marked } from 'marked';
import { Link, useLocation } from 'react-router-dom';
import { allPages, getPage } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Chip } from '../components/Chips';
import { shortDate } from '../lib/format';
import type { PageFull, PageSummary } from '../lib/types';

marked.setOptions({ gfm: true, breaks: false });

// [[page-key]] is the link form used across the knowledge base. Resolve it to a
// route when the key exists, and leave it as plain text when it does not, which
// is how the CLI's lint treats an unwritten page.
function resolveWikilinks(body: string, pages: PageSummary[]): string {
  const byKey = new Map(pages.map((p) => [p.key, p.path]));
  return body.replace(/\[\[([^\]|#]+)(?:[|#][^\]]*)?\]\]/g, (whole, key: string) => {
    const path = byKey.get(key.trim());
    return path ? `[${key.trim()}](/kb/${path})` : whole;
  });
}

export default function PageView() {
  const location = useLocation();
  const path = decodeURIComponent(location.pathname.replace(/^\/kb\//, ''));
  const loaded = useAsync<{ page: PageFull; pages: PageSummary[] }>(
    async () => {
      const [{ page }, pages] = await Promise.all([getPage(path), allPages()]);
      return { page, pages };
    },
    [path],
  );

  if (loaded.loading) return <p className="loading">Loading {path}...</p>;
  if (loaded.error) return <div className="error">{loaded.error}</div>;
  if (!loaded.data) return null;

  const { page, pages } = loaded.data;
  const html = marked.parse(resolveWikilinks(page.body ?? '', pages)) as string;

  return (
    <>
      <p className="sub">
        <Link to="/kb">Knowledge</Link> / <span className="rpath">{page.path}</span>
      </p>
      <h1>{page.title}</h1>
      <div className="detail-head" style={{ marginBottom: 10 }}>
        <Chip>{page.type}</Chip>
        {page.status ? <Chip>{page.status}</Chip> : null}
        {page.confidence ? <Chip>{page.confidence}</Chip> : null}
        {page.verified ? <Chip>verified {shortDate(page.verified)}</Chip> : null}
        {page.work_item ? (
          <Link to={`/item/${page.work_item}`}>
            <Chip className="kind-feature">work item {page.work_item}</Chip>
          </Link>
        ) : null}
      </div>
      {page.description ? <p className="sub">{page.description}</p> : null}
      <div className="panel md" dangerouslySetInnerHTML={{ __html: html }} />
      <p className="hint" style={{ marginTop: 14 }}>
        Mirrored from the RealityKnowledge repo. Edit the page there and run kb sync; this view follows.
      </p>
    </>
  );
}
